/**
 * Cart Staffelkorting — handles tier selection for server-rendered volume tiers.
 *
 * Tiers are rendered server-side in cart-tier-discount-control.liquid straight from
 * Shopify's catalog-resolved `item.variant.quantity_price_breaks` (the same source the
 * product page uses). This script only wires up the toggle/dropdown and lets a click on
 * a tier set the line quantity. It intentionally does NOT fetch tiers from the COA
 * `product-volume-discount` API or the product metafield: both are catalog-blind and
 * surfaced the wrong tiers to customers whose specific catalog has a fixed special price
 * with no volume discount (TAKE-21).
 *
 * Opening the menu is a plain button + dropdown span in cart-tier-discount-control.liquid,
 * toggled here via isDetailsOpen/setDetailsOpen (no native <details>/<summary> — that was
 * what a site-wide generic listener in nf-dialog.liquid reacted to and crashed on, since
 * this control never set the data-target attribute that listener expects).
 */
(function () {
  'use strict';

  const QUANTITY_SELECTOR_UPDATE = 'quantity-selector:update';
  const CART_UPDATE = 'cart:update';
  const LOG_PREFIX = '[cart-volume-pricing]';

  /**
   * @param {HTMLElement} row
   * @returns {{ quantityInput: HTMLInputElement; cartLine: number; selector: HTMLElement | null } | null}
   */
  const getCartQuantityControl = (row) => {
    const quantityInput = row.querySelector('input[data-cart-line]');
    if (!(quantityInput instanceof HTMLInputElement)) return null;

    const cartLine = Number(quantityInput.dataset.cartLine);
    if (!cartLine) return null;

    const selector = quantityInput.closest('quantity-selector-component');
    return { quantityInput, cartLine, selector: selector instanceof HTMLElement ? selector : null };
  };

  /**
   * @param {HTMLElement} row
   * @param {number} numericQuantity
   * @returns {boolean}
   */
  const applyCartLineQuantity = (row, numericQuantity) => {
    const control = getCartQuantityControl(row);
    if (!control) {
      console.warn(LOG_PREFIX, 'no quantity input found for row', row);
      return false;
    }

    const { quantityInput, cartLine } = control;
    const min = parseInt(quantityInput.min, 10) || 0;
    const max = quantityInput.max ? parseInt(quantityInput.max, 10) : null;

    let quantity = Math.max(numericQuantity, min);
    if (max != null && !Number.isNaN(max)) {
      quantity = Math.min(quantity, max);
    }

    quantityInput.value = String(quantity);

    const cartItems = row.closest('cart-items-component');
    if (cartItems && typeof cartItems.updateQuantity === 'function') {
      cartItems.updateQuantity({ line: cartLine, quantity, action: 'change' });
      return true;
    }

    quantityInput.dispatchEvent(
      new CustomEvent(QUANTITY_SELECTOR_UPDATE, {
        bubbles: true,
        detail: { quantity, cartLine },
      })
    );
    return true;
  };

  /** @type {HTMLElement | null} */
  let activeTierDetails = null;
  let populateGeneration = 0;

  const CART_TIER_MOBILE_QUERY = '(max-width: 749px)';
  const TIER_DROPDOWN_PORTAL_CLASS = 'cart-tier-dropdown--portaled';

  /**
   * "Open" state lives on the wrapper's classList instead of a native
   * <details>.open property — see the file header for why. This is the
   * single source of truth for every other function below.
   *
   * @param {HTMLElement} details
   * @returns {boolean}
   */
  const isDetailsOpen = (details) => Boolean(details) && details.classList.contains('is-open');

  /**
   * @param {HTMLElement} details
   * @param {boolean} isOpen
   */
  const setDetailsOpen = (details, isOpen) => {
    if (!(details instanceof HTMLElement)) return;
    details.classList.toggle('is-open', isOpen);
    const toggle = details.querySelector('[data-nf-cart-tier-toggle]');
    if (toggle) toggle.setAttribute('aria-expanded', String(isOpen));
  };

  /** @type {WeakMap<HTMLElement, { anchor: Comment, dropdown: HTMLElement }>} */
  const tierDropdownPortalState = new WeakMap();

  const isCartTierMobile = () => window.matchMedia(CART_TIER_MOBILE_QUERY).matches;

  const shouldPortalTierDropdown = () => isCartTierMobile() && Boolean(document.querySelector('.cart-page'));

  /**
   * @param {HTMLElement} details
   * @returns {HTMLElement | null}
   */
  const getTierDropdown = (details) => {
    const portaled = tierDropdownPortalState.get(details);
    if (portaled) return portaled.dropdown;

    const dropdown = details.querySelector('.volume-pricing-dropdown');
    return dropdown instanceof HTMLElement ? dropdown : null;
  };

  /**
   * @param {HTMLElement} details
   * @param {string} selector
   */
  const queryTierDetails = (details, selector) => {
    const dropdown = getTierDropdown(details);
    if (dropdown) {
      const match = dropdown.querySelector(selector);
      if (match) return match;
    }

    return details.querySelector(selector);
  };

  /**
   * @param {HTMLElement} details
   * @param {string} selector
   */
  const queryAllTierDetails = (details, selector) => {
    const dropdown = getTierDropdown(details);
    if (dropdown) {
      const matches = dropdown.querySelectorAll(selector);
      if (matches.length > 0) return matches;
    }

    return details.querySelectorAll(selector);
  };

  /**
   * @param {HTMLElement} details
   */
  const ensureTierDetailsId = (details) => {
    if (details.id) return details.id;

    const row = details.closest('tr.cart-items__table-row[data-line]');
    const line = row instanceof HTMLElement ? row.dataset.line : '';
    details.id = line ? `cart-tier-details-${line}` : `cart-tier-details-${Date.now()}`;
    return details.id;
  };

  /**
   * @param {HTMLElement} dropdown
   * @returns {boolean}
   */
  const isCartTierDropdown = (dropdown) =>
    dropdown.dataset.cartTierDropdown === 'true' ||
    dropdown.classList.contains(TIER_DROPDOWN_PORTAL_CLASS) ||
    Boolean(dropdown.closest('.cart-items__tier-discount-host'));

  /**
   * @param {HTMLElement} details
   * @param {HTMLElement} row
   * @param {HTMLElement} dropdown
   */
  const syncTierDropdownAssociation = (details, row, dropdown) => {
    const detailsId = ensureTierDetailsId(details);
    dropdown.dataset.tierDetailsId = detailsId;
    dropdown.dataset.tierLine = row.dataset.line || '';
    dropdown.dataset.tierVariantId = row.dataset.variantId || '';
  };

  /**
   * @param {HTMLElement} dropdown
   * @returns {HTMLElement | null}
   */
  const getTierDetailsForDropdown = (dropdown) => {
    const detailsId = dropdown.dataset.tierDetailsId || '';
    if (detailsId) {
      const details = document.getElementById(detailsId);
      if (details instanceof HTMLElement) return details;
    }

    const details = dropdown.closest('.cart-items__tier-discount-details');
    if (details instanceof HTMLElement) return details;

    if (activeTierDetails instanceof HTMLElement) return activeTierDetails;

    return null;
  };

  /**
   * @param {HTMLElement} dropdown
   * @returns {HTMLElement | null}
   */
  const getTierRowForDropdown = (dropdown, details) => {
    if (details instanceof HTMLElement) {
      const row = details.closest('tr.cart-items__table-row[data-variant-id]');
      if (row instanceof HTMLElement) return row;
    }

    const line = dropdown.dataset.tierLine || '';
    if (line) {
      const row = document.querySelector(`tr.cart-items__table-row[data-line="${line}"]`);
      if (row instanceof HTMLElement) return row;
    }

    const variantId = dropdown.dataset.tierVariantId || '';
    if (variantId) {
      const row = document.querySelector(`tr.cart-items__table-row[data-variant-id="${variantId}"]`);
      if (row instanceof HTMLElement) return row;
    }

    return null;
  };

  /**
   * @param {HTMLElement} dropdown
   * @param {HTMLElement} option
   * @returns {{ row: HTMLElement, option: HTMLElement, details: HTMLElement } | null}
   */
  const resolveVolumeOptionClick = (dropdown, option) => {
    if (!(dropdown instanceof HTMLElement) || !(option instanceof HTMLElement)) return null;
    if (!isCartTierDropdown(dropdown)) return null;

    const details = getTierDetailsForDropdown(dropdown);
    if (!(details instanceof HTMLElement)) return null;

    const row = getTierRowForDropdown(dropdown, details);
    if (!(row instanceof HTMLElement)) return null;

    return { row, option, details };
  };

  /**
   * @param {Event} event
   */
  const handleDropdownOptionClick = (event) => {
    if (!(event.currentTarget instanceof HTMLElement)) return;
    if (!(event.target instanceof Element)) return;

    const option = event.target.closest('.volume-option');
    if (!(option instanceof HTMLElement)) return;

    const context = resolveVolumeOptionClick(event.currentTarget, option);
    if (!context) return;

    event.preventDefault();
    event.stopPropagation();
    selectVolumeOption(context.row, context.option, context.details);
  };

  /**
   * Delegated click on the dropdown container — survives innerHTML updates to options.
   *
   * @param {HTMLElement} dropdown
   * @param {HTMLElement} details
   * @param {HTMLElement} row
   */
  const ensureDropdownClickDelegation = (dropdown, details, row) => {
    if (!(dropdown instanceof HTMLElement)) return;
    if (!(details instanceof HTMLElement) || !(row instanceof HTMLElement)) return;

    dropdown.dataset.cartTierDropdown = 'true';
    syncTierDropdownAssociation(details, row, dropdown);

    if (dropdown.dataset.tierClickDelegated === 'true') return;
    dropdown.dataset.tierClickDelegated = 'true';
    dropdown.addEventListener('click', handleDropdownOptionClick, true);
  };

  const TIER_PORTAL_INLINE_STYLES = {
    position: 'fixed',
    top: '0px',
    left: '0px',
    right: 'auto',
    bottom: 'auto',
    margin: '0',
    zIndex: '2147483647',
    display: 'block',
    background: '#fff',
    border: '1px solid #ddd',
    borderRadius: '8px',
    boxShadow: '0 4px 12px rgb(0 0 0 / 15%)',
    minWidth: '220px',
    maxWidth: 'calc(100vw - 32px)',
    pointerEvents: 'auto',
  };

  /**
   * @param {HTMLElement} dropdown
   */
  const applyPortalDropdownStyles = (dropdown) => {
    for (const [property, value] of Object.entries(TIER_PORTAL_INLINE_STYLES)) {
      dropdown.style.setProperty(
        property.replace(/[A-Z]/g, (match) => `-${match.toLowerCase()}`),
        value,
        'important'
      );
    }
  };

  /**
   * @param {HTMLElement} dropdown
   */
  const clearPortalDropdownStyles = (dropdown) => {
    for (const property of Object.keys(TIER_PORTAL_INLINE_STYLES)) {
      dropdown.style.removeProperty(property.replace(/[A-Z]/g, (match) => `-${match.toLowerCase()}`));
    }
    dropdown.style.removeProperty('width');
    dropdown.style.removeProperty('height');
    dropdown.style.removeProperty('max-height');
    dropdown.style.removeProperty('overflow-y');
  };

  /**
   * @param {HTMLElement} details
   */
  const portalTierDropdown = (details) => {
    if (!shouldPortalTierDropdown() || tierDropdownPortalState.has(details)) return;

    const dropdown = details.querySelector('.volume-pricing-dropdown');
    if (!(dropdown instanceof HTMLElement)) return;

    const anchor = document.createComment('cart-tier-dropdown-anchor');
    const row = details.closest('tr.cart-items__table-row[data-variant-id]');
    dropdown.parentNode?.insertBefore(anchor, dropdown);
    if (row instanceof HTMLElement) {
      ensureDropdownClickDelegation(dropdown, details, row);
    } else {
      dropdown.dataset.tierDetailsId = ensureTierDetailsId(details);
      dropdown.dataset.cartTierDropdown = 'true';
    }
    document.body.append(dropdown);
    dropdown.classList.add(TIER_DROPDOWN_PORTAL_CLASS);
    applyPortalDropdownStyles(dropdown);
    tierDropdownPortalState.set(details, { anchor, dropdown });
  };

  /**
   * @param {HTMLElement} details
   */
  const restoreTierDropdown = (details) => {
    const state = tierDropdownPortalState.get(details);
    if (!state) return;

    const { anchor, dropdown } = state;

    clearPortalDropdownStyles(dropdown);

    if (anchor.parentNode) {
      anchor.parentNode.insertBefore(dropdown, anchor);
      anchor.remove();
    } else {
      const toggle = details.querySelector('[data-nf-cart-tier-toggle]');
      toggle?.insertAdjacentElement('afterend', dropdown);
    }

    dropdown.classList.remove(TIER_DROPDOWN_PORTAL_CLASS);
    dropdown.removeAttribute('data-tier-details-id');
    tierDropdownPortalState.delete(details);
  };

  const restoreAllTierDropdowns = () => {
    document.querySelectorAll('.cart-items__tier-discount-details').forEach((details) => {
      if (details instanceof HTMLElement) restoreTierDropdown(details);
    });

    document.querySelectorAll(`body > .volume-pricing-dropdown.${TIER_DROPDOWN_PORTAL_CLASS}`).forEach((dropdown) => {
      dropdown.remove();
    });
  };

  const clearAllTierDropdownPositions = () => {
    document.querySelectorAll('.cart-items__tier-discount-details .volume-pricing-dropdown').forEach((dropdown) => {
      if (!(dropdown instanceof HTMLElement)) return;
      dropdown.style.removeProperty('top');
      dropdown.style.removeProperty('left');
      dropdown.style.removeProperty('width');
    });
  };

  /**
   * @param {HTMLElement} details
   */
  const clearTierDropdownPosition = (details) => {
    const dropdown = getTierDropdown(details);
    if (!dropdown) return;

    dropdown.style.removeProperty('top');
    dropdown.style.removeProperty('left');
    dropdown.style.removeProperty('width');
  };

  /**
   * @param {HTMLElement} details
   */
  const positionActiveTierDropdown = (details) => {
    if (!isCartTierMobile()) {
      clearTierDropdownPosition(details);
      return;
    }

    if (!(details instanceof HTMLElement) || !isDetailsOpen(details)) return;

    const host = details.closest('.cart-items__tier-discount-host');
    const dropdown = getTierDropdown(details);
    if (!(host instanceof HTMLElement) || !dropdown) return;

    const hostRect = host.getBoundingClientRect();
    const viewportPadding = 16;
    const viewportHeight = window.visualViewport?.height ?? window.innerHeight;
    const dropdownWidth = Math.min(280, window.innerWidth - viewportPadding * 2);
    let left = hostRect.left;

    if (left + dropdownWidth > window.innerWidth - viewportPadding) {
      left = Math.max(viewportPadding, window.innerWidth - viewportPadding - dropdownWidth);
    }

    left = Math.max(viewportPadding, left);

    const spaceBelow = viewportHeight - hostRect.bottom - viewportPadding;
    const spaceAbove = hostRect.top - viewportPadding;
    const maxHeight = Math.max(160, Math.max(spaceBelow, spaceAbove));

    const openBelow = spaceBelow >= 160 || spaceBelow >= spaceAbove;
    const top = openBelow ? Math.round(hostRect.bottom + 4) : Math.max(viewportPadding, Math.round(hostRect.top - 4 - Math.min(maxHeight, spaceAbove)));

    dropdown.style.setProperty('top', `${top}px`, 'important');
    dropdown.style.setProperty('left', `${Math.round(left)}px`, 'important');
    dropdown.style.setProperty('width', `${dropdownWidth}px`, 'important');
    dropdown.style.setProperty('max-height', `${Math.round(openBelow ? spaceBelow : spaceAbove)}px`, 'important');
    dropdown.style.setProperty('overflow-y', 'auto', 'important');
  };

  const schedulePositionActiveTierDropdown = () => {
    if (!(activeTierDetails instanceof HTMLElement)) return;

    requestAnimationFrame(() => {
      if (!(activeTierDetails instanceof HTMLElement)) return;
      positionActiveTierDropdown(activeTierDetails);
    });
  };

  const clearDropdownDisplayLock = (details) => {
    const dropdown = getTierDropdown(details);
    if (dropdown instanceof HTMLElement) {
      dropdown.style.removeProperty('display');
    }
  };

  /**
   * @param {HTMLElement} row
   * @param {HTMLElement} option
   * @param {HTMLElement} details
   */
  const selectVolumeOption = (row, option, details) => {
    const quantity = option.dataset.quantity;
    if (!quantity) return;

    const numericQuantity = parseInt(quantity.replace('+', ''), 10);
    if (!numericQuantity || numericQuantity <= 0) return;

    const updated = applyCartLineQuantity(row, numericQuantity);
    if (!updated) return;

    row.querySelectorAll('.volume-option').forEach((opt) => {
      opt.classList.toggle('selected', opt === option);
    });

    closeTierDiscountDetails(details);
  };

  /**
   * @param {HTMLElement} details
   */
  const syncTierDropdownVisibility = (details) => {
    if (!(details instanceof HTMLElement)) return;

    const dropdown = getTierDropdown(details);
    if (!(dropdown instanceof HTMLElement)) return;

    // Always driven by an explicit inline !important display, both open and
    // closed — there's no <details>[open] CSS selector backing this anymore,
    // so nothing else in the cascade can be relied on to show it.
    dropdown.style.setProperty('display', isDetailsOpen(details) ? 'block' : 'none', 'important');
  };

  /**
   * @param {HTMLElement | null} details
   */
  const setActiveTierDetails = (details) => {
    document.querySelectorAll('tr.cart-items__table-row[data-tier-active]').forEach((row) => {
      row.removeAttribute('data-tier-active');
    });

    activeTierDetails = details;

    if (!(details instanceof HTMLElement)) {
      restoreAllTierDropdowns();
      clearAllTierDropdownPositions();
      return;
    }

    const row = details.closest('tr.cart-items__table-row[data-variant-id]');
    if (row instanceof HTMLElement) {
      row.setAttribute('data-tier-active', '');

      const dropdown = getTierDropdown(details);
      if (dropdown instanceof HTMLElement) {
        ensureDropdownClickDelegation(dropdown, details, row);
      }
    }

    portalTierDropdown(details);
    syncTierDropdownVisibility(details);
    schedulePositionActiveTierDropdown();
  };

  /**
   * @param {HTMLElement} details
   */
  const closeTierDiscountDetails = (details) => {
    if (!(details instanceof HTMLElement)) return;

    setDetailsOpen(details, false);
    restoreTierDropdown(details);
    syncTierDropdownVisibility(details);
    clearTierDropdownPosition(details);

    if (activeTierDetails === details) {
      setActiveTierDetails(null);
    }
  };

  const closeAllTierDiscountDetails = () => {
    document.querySelectorAll('.cart-items__tier-discount-details').forEach((other) => {
      if (!(other instanceof HTMLElement) || !isDetailsOpen(other)) return;
      setDetailsOpen(other, false);
      restoreTierDropdown(other);
      syncTierDropdownVisibility(other);
      clearTierDropdownPosition(other);
    });

    restoreAllTierDropdowns();
    setActiveTierDetails(null);
  };

  /**
   * @param {HTMLElement} details
   * @param {number} generation
   */
  const populateTierDiscountDetails = (details, generation) => {
    if (generation !== populateGeneration || activeTierDetails !== details || !isDetailsOpen(details)) return;

    const row = details.closest('tr.cart-items__table-row[data-variant-id]');
    if (!(row instanceof HTMLElement)) return;

    clearDropdownDisplayLock(details);

    if (!queryTierDetails(details, '.volume-option')) {
      // Tiers are rendered server-side; a line with no options simply has no tiered pricing.
      showEmptyTierMessage(details);
    } else {
      syncSelectedTier(row, details);
    }

    if (generation !== populateGeneration || activeTierDetails !== details || !isDetailsOpen(details)) return;

    syncTierDropdownVisibility(details);
    schedulePositionActiveTierDropdown();
  };

  /**
   * @param {HTMLElement} details
   */
  const openExclusiveTierDiscount = (details) => {
    populateGeneration += 1;
    const generation = populateGeneration;

    closeAllTierDiscountDetails();

    setDetailsOpen(details, true);
    setActiveTierDetails(details);
    syncTierDropdownVisibility(details);

    populateTierDiscountDetails(details, generation);
  };

  const syncSelectedTier = (row, details) => {
    if (!(details instanceof HTMLElement)) return;

    const control = getCartQuantityControl(row);
    if (!control) return;

    const currentQty = parseInt(control.quantityInput.value, 10) || 0;

    // Read tiers straight from the server-rendered options (no client-side tier source).
    const options = Array.from(queryAllTierDetails(details, '.volume-option'));

    let bestQuantity = null;
    for (const opt of options) {
      const tierQty = parseInt((opt.dataset.quantity || '').replace('+', ''), 10);
      if (!Number.isNaN(tierQty) && currentQty >= tierQty) bestQuantity = opt.dataset.quantity;
    }

    options.forEach((opt) => {
      opt.classList.toggle('selected', bestQuantity !== null && opt.dataset.quantity === bestQuantity);
    });
  };

  const fillDetailsDropdown = (details, row) => {
    if (!(details instanceof HTMLElement) || !(row instanceof HTMLElement)) return false;

    if (isDetailsOpen(details)) {
      clearDropdownDisplayLock(details);
    }

    // Tiers are rendered server-side from catalog-resolved quantity_price_breaks; if none are
    // present for this line, there is nothing to show.
    if (!queryTierDetails(details, '.volume-option')) return false;

    const dropdown = getTierDropdown(details);
    if (dropdown instanceof HTMLElement) {
      ensureDropdownClickDelegation(dropdown, details, row);
    }

    syncSelectedTier(row, details);
    syncTierDropdownVisibility(details);
    return true;
  };

  const setHostVisibility = (details, visible) => {
    const host = details.closest('.cart-items__tier-discount-host');
    if (!(host instanceof HTMLElement)) return;
    host.hidden = !visible;
  };

  const fillAllPendingDropdowns = () => {
    document.querySelectorAll('.cart-items__tier-discount-details').forEach((details) => {
      const row = details.closest('.cart-items__table-row[data-variant-id]');
      if (!(row instanceof HTMLElement)) return;

      const filled = fillDetailsDropdown(details, row);
      // Only show the Staffelkorting button on items that actually have tiered pricing.
      setHostVisibility(details, filled);
      syncTierDropdownVisibility(details);
    });
  };

  const showEmptyTierMessage = (details) => {
    const dropdown = getTierDropdown(details);
    if (!dropdown) return;

    dropdown.innerHTML =
      '<div class="volume-options"><p class="volume-options__empty">Geen staffelkorting beschikbaar</p></div>';
    dropdown.removeAttribute('data-volume-pending');
    syncTierDropdownVisibility(details);
  };

  const getTierDiscountDetails = (target) => {
    if (target instanceof HTMLElement && target.classList.contains('cart-items__tier-discount-details')) {
      return target;
    }

    if (!(target instanceof Element)) return null;

    const details = target.closest('.cart-items__tier-discount-details');
    return details instanceof HTMLElement ? details : null;
  };

  const handleTierDiscountToggleClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const toggle = event.target.closest('[data-nf-cart-tier-toggle]');
    if (!toggle) return;

    const details = toggle.closest('.cart-items__tier-discount-details');
    if (!(details instanceof HTMLElement)) return;

    event.preventDefault();

    if (details === activeTierDetails && isDetailsOpen(details)) {
      closeTierDiscountDetails(details);
      return;
    }

    openExclusiveTierDiscount(details);
  };

  const handleTierDiscountCloseClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const closeButton = event.target.closest('[data-nf-cart-tier-close]');
    if (!closeButton) return;

    const details = getTierDiscountDetails(closeButton);
    if (!details) return;

    event.preventDefault();
    event.stopPropagation();
    closeTierDiscountDetails(details);
  };

  const handleTierDiscountClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const dropdown = target.closest('[data-cart-tier-dropdown="true"]');
    if (!(dropdown instanceof HTMLElement)) return;

    const option = target.closest('.volume-option');
    if (!(option instanceof HTMLElement)) return;

    const context = resolveVolumeOptionClick(dropdown, option);
    if (!context) return;

    event.preventDefault();
    event.stopPropagation();
    selectVolumeOption(context.row, context.option, context.details);
  };

  const init = () => {
    if (!document.documentElement.dataset.cartVolumePricingListeners) {
      document.documentElement.dataset.cartVolumePricingListeners = 'true';
      document.addEventListener('click', handleTierDiscountToggleClick, true);
      document.addEventListener('click', handleTierDiscountCloseClick, true);
      document.addEventListener('click', handleTierDiscountClick, true);
      // Mirrors nf-dialog.js's click-outside handling for the volume-discount
      // popover: clicking anywhere outside an open tier-discount control closes it.
      document.addEventListener('click', (event) => {
        if (event.target instanceof Element && event.target.closest('.cart-items__tier-discount-details')) return;
        closeAllTierDiscountDetails();
      });
      window.addEventListener('resize', schedulePositionActiveTierDropdown);
      window.addEventListener('scroll', schedulePositionActiveTierDropdown, true);
    }

    closeAllTierDiscountDetails();

    document.querySelectorAll('.cart-items__tier-discount-details').forEach((details) => {
      if (!(details instanceof HTMLElement)) return;
      const row = details.closest('.cart-items__table-row[data-variant-id]');
      syncTierDropdownVisibility(details);

      const hasOptions = Boolean(queryTierDetails(details, '.volume-option'));
      // Hide hosts with no server-rendered tiers so guests / items without the promo never
      // see an empty Staffelkorting button.
      setHostVisibility(details, hasOptions);

      if (row instanceof HTMLElement) {
        const dropdown = getTierDropdown(details);
        if (dropdown instanceof HTMLElement) {
          ensureDropdownClickDelegation(dropdown, details, row);
        }
      }
    });

    fillAllPendingDropdowns();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener(CART_UPDATE, () => {
    closeAllTierDiscountDetails();
    requestAnimationFrame(() => {
      fillAllPendingDropdowns();
    });
  });

  document.addEventListener(QUANTITY_SELECTOR_UPDATE, (event) => {
    const line = event.detail?.cartLine;
    if (!line) return;

    const row = document.querySelector(`.cart-items__table-row[data-line="${line}"]`);
    const details = row?.querySelector('.cart-items__tier-discount-details');
    if (row instanceof HTMLElement && details instanceof HTMLElement) {
      syncSelectedTier(row, details);
    }
  });
})();
